// Vendor imports
const _ = require('lodash')
const classNames = require('classnames')
const PropTypes = require('prop-types')
const React = require('react')
// Browser-only vendor imports
let Masonry
if (typeof window === 'object') {
  Masonry = require('masonry-layout')
}
// Shared imports
const { TYPES: modelTypes } = require('shared/model-types')
const BemEntity = require('shared/bem')
// Client V2 common imports
const tag = require('client-v2/common/utils/hyperscript')
const ArticleCard = require('client-v2/common/components/cards/article')
const CollectionCard = require('client-v2/common/components/cards/collection')
const UserCard = require('client-v2/common/components/cards/user')
const { FeedItemRecord } = require('client-v2/common/containers/feed/records')

const bem = BemEntity('CardGrid')

class CardGrid extends React.PureComponent {
  constructor(props) {
    super(props)

    this.gridRef = React.createRef()
  }

  componentDidMount() {
    this.setupMasonryLayout()
  }

  componentWillUnmount() {
    this.teardownMasonryLayout()
  }

  componentDidUpdate() {
    this.updateMasonryLayout()
  }

  getCurrentChildren() {
    return Array.from(this.gridRef.current.children)
  }

  setupMasonryLayout() {
    if (Masonry && this.props.isMasonryEnabled) {
      this.masonry = new Masonry(this.gridRef.current, {
        itemSelector: bem.elementSelector('item'),
        percentPosition: true
      })
      this.masonryItems = this.getCurrentChildren()
    }
  }

  teardownMasonryLayout() {
    if (this.masonry) {
      this.masonry.destroy()
      delete this.masonry
      delete this.masonryItems
    }
  }

  updateMasonryLayout() {
    if (this.masonry) {
      const prevChildren = this.masonryItems
      const currentChildren = this.getCurrentChildren()
      const removedChildren = _.difference(prevChildren, currentChildren)
      const addedChildren = _.difference(currentChildren, prevChildren)
      if (removedChildren.length) {
        this.masonry.remove(removedChildren)
      }
      if (addedChildren.length) {
        addedChildren.forEach(el => {
          el.className = el.className.replace(
            bem.element('item'),
            bem.element('item', 'added')
          )
        })
        this.masonry.addItems(addedChildren)
      }
      this.masonry.layout()
      this.masonryItems = currentChildren
    }
  }

  wrapChildren(children) {
    return React.Children.map(children, child => {
      return React.cloneElement(child, {
        className: classNames(
          bem.element('item', 'child'),
          child.className
        )
      })
    })
  }

  renderArticleCard({ type, id, article, meta, pageContext }, index) {
    return (
      tag(ArticleCard, {
        key: `${type}:${id}`,
        // Flags
        isHrefsEnabled: this.props.isHrefsEnabled,
        // Values
        article,
        className: bem.element('item'),
        itemIndex: index,
        pageContext,
        recReason: meta.recReason,
        // Callbacks
        onAddClick: this.props.onArticleAddClick,
        onCardClick: this.props.onArticleCardClick,
        onCountClick: this.props.onArticleCountClick,
        onDomainClick: this.props.onArticleDomainClick,
        onEmbedClick: this.props.onArticleEmbedClick,
        onMoreClick: this.props.onArticleMoreClick,
        onPreviewClick: this.props.onArticlePreviewClick,
        onSeen: this.props.onArticleSeen
      })
    )
  }

  renderCollectionCard({ type, id, collection, pageContext }) {
    return (
      tag(CollectionCard, {
        key: `${type}:${id}`,
        // Flags
        hasSubscribersCount: !this.props.isCollectionSubscribersCountHidden,
        isHrefsEnabled: this.props.isHrefsEnabled,
        isSelf: collection['user_id'] === this.props.selfUserId,
        // Values
        className: bem.element('item'),
        collection,
        pageContext,
        // Callbacks
        onCardClick: this.props.onCollectionCardClick,
        onCountClick: this.props.onCollectionCountClick,
        onFollowClick: this.props.onCollectionFollowClick,
        onUsernameClick: this.props.onCollectionUsernameClick
      })
    )
  }

  renderUserCard({ type, id, user, pageContext }) {
    return (
      tag(this.props.userCardComponent || UserCard, {
        key: `${type}:${id}`,
        // Flags
        hasFollowerCount: !this.props.isUserFollowerCountHidden,
        isHrefsEnabled: this.props.isHrefsEnabled,
        isSelf: user.id === this.props.selfUserId,
        // Values
        className: bem.element('item'),
        pageContext,
        user,
        // Callbacks
        onCardClick: this.props.onUserCardClick,
        onFollowClick: this.props.onUserFollowClick
      })
    )
  }

  renderItems() {
    return this.props.items.map((item, index) => {
      switch (item.type) {
        case modelTypes.ARTICLE:
          return this.renderArticleCard(item, index)
        case modelTypes.MIX:
          return this.renderCollectionCard(item, index)
        case modelTypes.USER:
          return this.renderUserCard(item, index)
        default:
          return null
      }
    })
  }

  renderLeading() {
    const { renderLeading } = this.props
    if (renderLeading) {
      return this.wrapChildren(renderLeading())
    }
  }

  renderTrailing() {
    const { renderTrailing } = this.props
    if (renderTrailing) {
      return this.wrapChildren(renderTrailing())
    }
  }

  render() {
    const { props } = this
    const masonry = props.isMasonryEnabled
    return (
      tag('div', {
        ref: this.gridRef,
        className: classNames(
          props.className,
          bem.block({ masonry })
        )
      },
        this.renderLeading(),
        this.renderItems(),
        this.renderTrailing()
      )
    )
  }
}

CardGrid.propTypes = {
  // Flags
  isCollectionSubscribersCountHidden: PropTypes.bool,
  isHrefsEnabled: PropTypes.bool,
  isMasonryEnabled: PropTypes.bool,
  isUserFollowerCountHidden: PropTypes.bool,
  // Values
  className: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape(FeedItemRecord.propTypes)
  ).isRequired,
  selfUserId: PropTypes.number,
  // Components
  userCardComponent: PropTypes.elementType,
  // Callbacks
  onArticleAddClick: PropTypes.func,
  onArticleCardClick: PropTypes.func,
  onArticleCountClick: PropTypes.func,
  onArticleDomainClick: PropTypes.func,
  onArticleEmbedClick: PropTypes.func,
  onArticleMoreClick: PropTypes.func,
  onArticlePreviewClick: PropTypes.func,
  onArticleSeen: PropTypes.func,
  onCollectionCardClick: PropTypes.func,
  onCollectionCountClick: PropTypes.func,
  onCollectionFollowClick: PropTypes.func,
  onCollectionUsernameClick: PropTypes.func,
  onUserCardClick: PropTypes.func,
  onUserFollowClick: PropTypes.func,
  renderLeading: PropTypes.func,
  renderTrailing: PropTypes.func
}

CardGrid.defaultProps = {
  isHrefsEnabled: true,
  isMasonryEnabled: true
}

module.exports = CardGrid
